import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiButtonComponent, QaiIconComponent } from '@qai/ui';
import { Subject, takeUntil, throttleTime } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ControlService } from '../../library/control.service';
import { CallStoreService } from '../../library/call-store/call-store.service';
import { PlayerService } from '../../library/player.service';

@Component({
  host: {
    class: 'theme-dark',
  },
  imports: [
    CommonModule,
    QaiIconComponent,
    QaiButtonComponent,
    TranslocoModule,
  ],
  selector: 'app-visual-aid-next-page-navigator',
  standalone: true,
  templateUrl: './visual-aid-next-page-navigator.component.html',
  styleUrls: ['./visual-aid-next-page-navigator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VisualAidNextPageNavigatorComponent implements OnChanges {
  @Output() readonly nextClicked = new EventEmitter();
  @Output() readonly fullScreenClicked = new EventEmitter();
  @Input({ required: true }) canContinueNext: boolean = false;
  @Input() isAvatarSpeaking: boolean | undefined = false;

  private nextClick$ = new Subject<Event>();
  private destroy$ = new Subject<void>();
  private isNewClipWithAvatarSpeech: boolean = false;
  private lastClipIndex: number = -1;

  constructor(
    private controlService: ControlService,
    private callStore: CallStoreService,
    private translocoService: TranslocoService,
    private playerService: PlayerService
  ) {}

  ngOnInit() {
    this.nextClick$
      .pipe(throttleTime(1000), takeUntil(this.destroy$))
      .subscribe((event) => {
        this.nextClicked.emit(event);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateStates();
  }

  protected get isContinueButtonDisabled() {
    return this.isAvatarSpeaking || this.isNewClipWithAvatarSpeech;
  }

  private updateStates(): void {
    if (this.controlService?.isRealTimeCall) {
      const realtimeService = this.controlService?.realtimeService;
      if (!realtimeService) return;

      this.processClipChange(
        realtimeService.guidedIndex,
        realtimeService.guidedClips
      );
    } else {
      if (!this.playerService) return;

      this.processClipChange(
        this.playerService.currentClipIndex,
        this.playerService.callClips,
        true
      );
    }
  }

  private processClipChange(
    currentIndex: number,
    clips: any[],
    isSynthesia = false
  ): void {
    if (currentIndex !== this.lastClipIndex) {
      this.lastClipIndex = currentIndex;

      if (!clips || currentIndex >= clips.length) return;

      const currentClip = clips[currentIndex];

      const texts: string[] = isSynthesia
        ? [currentClip?.clip?.agentSays, currentClip?.clipLoop?.agentSays]
        : [currentClip?.agentSays];

      this.isNewClipWithAvatarSpeech = texts.some(
        (text) => !!(text && text.trim().length > 0)
      );
    }

    if (this.isAvatarSpeaking) {
      this.isNewClipWithAvatarSpeech = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected handleNextClick(event: Event) {
    this.nextClick$.next(event);
  }

  handleFullScreenClick() {
    this.fullScreenClicked.emit();
  }

  protected get fullScreenButtonText() {
    const [collapse, fullScreen] = [
      'COLLAPSE',
      'FULL_SCREEN'
    ].map(key => this.translocoService.translate(key, {}, 'call'));
    return this.callStore.getIsFullScreen() ? collapse : fullScreen;
  }
}
